/**
 * SkinnrCompactView.tsx - Compact, colorful theme settings view
 * 
 * CHANGE HISTORY:
 * - 251105_1911 [pending] danielbeer: Created compact view inspired by Modulr's colorful design
 * 
 * Compact, space-efficient view for Skinnr theme settings with:
 * - Color swatches for visual theme preview
 * - Minimal padding and tight spacing
 * - Badge-style indicators
 * - Grid layout for maximum density
 * - Interactive color previews
 */

import React from 'react';
import { useSkinnr } from '../SkinnrContext';
import './SkinnrCompactView.css';

export interface SkinnrCompactViewProps {
  /** Show theme switcher */
  showThemeSwitcher?: boolean;
  /** Show color customization */
  showColorCustomization?: boolean;
  /** Compact mode (even tighter spacing) */
  ultraCompact?: boolean;
  /** Maximum width */
  maxWidth?: string;
}

interface Skin {
  id: string;
  name: string;
  description?: string;
  variables?: Record<string, string>;
  classes?: Record<string, string>;
  layout?: Record<string, any>;
  css?: string;
  metadata?: Record<string, any>;
}

export const SkinnrCompactView: React.FC<SkinnrCompactViewProps> = ({
  showThemeSwitcher = true,
  showColorCustomization = true,
  ultraCompact = false,
  maxWidth = '100%',
}) => {
  const skinnrContext = useSkinnr() as any; // Type assertion for now - context needs proper typing
  
  // Extract values with proper typing
  const currentSkin: Skin | null = skinnrContext.getActiveSkin?.() || null;
  const currentMode: string = skinnrContext.getCurrentMode?.() || 'light';
  const availableSkins: Skin[] = skinnrContext.getAllSkins?.() || [];
  const applySkin: (skinId: string) => void = skinnrContext.applySkin || (() => {});
  const toggleMode: () => void = skinnrContext.toggleMode || (() => {});

  const handleThemeChange = (skinId: string) => {
    applySkin(skinId);
  };

  const handleModeToggle = () => {
    toggleMode();
  };

  // Get current theme colors from CSS variables
  const getColorValue = (varName: string): string => {
    if (typeof window === 'undefined') return '#000';
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  };

  const colorGroups = [
    {
      name: 'Primary',
      colors: [
        { label: 'Primary', var: '--color-primary' },
        { label: 'Hover', var: '--color-primary-hover' },
        { label: 'Active', var: '--color-primary-active' },
      ],
    },
    {
      name: 'Semantic',
      colors: [
        { label: 'Success', var: '--color-success' },
        { label: 'Warning', var: '--color-warning' },
        { label: 'Error', var: '--color-error' },
        { label: 'Info', var: '--color-info' },
      ],
    },
    {
      name: 'Text',
      colors: [
        { label: 'Primary', var: '--text-primary' },
        { label: 'Secondary', var: '--text-secondary' },
        { label: 'Tertiary', var: '--text-tertiary' },
      ],
    },
    {
      name: 'Background',
      colors: [
        { label: 'Primary', var: '--bg-primary' },
        { label: 'Secondary', var: '--bg-secondary' },
        { label: 'Tertiary', var: '--bg-tertiary' },
      ],
    },
  ];

  return (
    <div 
      className={`skinnr-compact-view ${ultraCompact ? 'ultra-compact' : ''}`}
      style={{ maxWidth }}
    >
      {showThemeSwitcher && (
        <div className="compact-section">
          <div className="section-header">
            <span className="section-icon">🎨</span>
            <h3 className="section-title">Theme</h3>
          </div>
          
          <div className="theme-grid">
            {availableSkins.map((skin: Skin) => (
              <button
                key={skin.id}
                className={`theme-button ${currentSkin?.id === skin.id ? 'active' : ''}`}
                onClick={() => handleThemeChange(skin.id)}
                title={skin.description || skin.name}
              >
                <span className="theme-name">{skin.name}</span>
                {currentSkin?.id === skin.id && (
                  <span className="active-indicator">✓</span>
                )}
              </button>
            ))}
          </div>

          <div className="mode-toggle-container">
            <button
              className={`mode-toggle ${currentMode === 'dark' ? 'dark-active' : 'light-active'}`}
              onClick={handleModeToggle}
              title={`Switch to ${currentMode === 'dark' ? 'light' : 'dark'} mode`}
            >
              <span className="mode-icon">{currentMode === 'dark' ? '🌙' : '☀️'}</span>
              <span className="mode-label">{currentMode === 'dark' ? 'Dark' : 'Light'}</span>
            </button>
          </div>
        </div>
      )}

      {showColorCustomization && (
        <div className="compact-section">
          <div className="section-header">
            <span className="section-icon">🎨</span>
            <h3 className="section-title">Colors</h3>
          </div>

          <div className="color-groups">
            {colorGroups.map((group) => (
              <div key={group.name} className="color-group">
                <div className="group-label">{group.name}</div>
                <div className="color-swatches">
                  {group.colors.map((color) => {
                    const colorValue = getColorValue(color.var);
                    return (
                      <div
                        key={color.var}
                        className="color-swatch"
                        title={`${color.label}: ${colorValue}`}
                      >
                        <div
                          className="swatch-color"
                          style={{ backgroundColor: colorValue }}
                        />
                        <span className="swatch-label">{color.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="compact-section">
        <div className="section-header">
          <span className="section-icon">ℹ️</span>
          <h3 className="section-title">Current Theme</h3>
        </div>
        
        <div className="theme-info">
          <div className="info-row">
            <span className="info-label">Active:</span>
            <span className="info-value badge">{currentSkin?.name || 'None'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Mode:</span>
            <span className="info-value badge mode-badge">{currentMode}</span>
          </div>
          {currentSkin?.description && (
            <div className="info-row">
              <span className="info-label">About:</span>
              <span className="info-value description">{currentSkin.description}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkinnrCompactView;
